import React from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  ArrowForward as NextIcon,
  North as HigherIcon,
  South as LowerIcon,
  Style as CardIcon,
  SwapVert as PredictIcon,
  Lock as LockIcon,
  Star as PointIcon,
  Loop as RoundsIcon,
} from "@mui/icons-material";
import { HOL, holFloat, holPrimaryBtnSx, holPulse } from "../../../UserSide/pages/holTheme";
import { CardImage, Eyebrow, StepShell, holGradientText } from "./holHostUi";

const ArrowBadge = ({ Icon, color, sx }) => (
  <Box
    sx={{
      position: "absolute",
      width: 52,
      height: 52,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      bgcolor: color,
      color: "#0a0a0a",
      boxShadow: `0 10px 30px ${color}66`,
      animation: `${holFloat} 3s ease-in-out infinite`,
      ...sx,
    }}
  >
    <Icon sx={{ fontSize: 30 }} />
  </Box>
);

const HolIntroStep = ({ numberOfCards, onNext, waiting = false }) => {
  const calls = Math.max(1, numberOfCards - 1);
  const concept = [
    { Icon: CardIcon, text: "A card is shown to the whole room." },
    {
      Icon: PredictIcon,
      text: (
        <>
          The hot seat player predicts whether the next card is{" "}
          <Box component="strong" sx={{ color: HOL.higher }}>HIGHER</Box> or{" "}
          <Box component="strong" sx={{ color: HOL.lower }}>LOWER</Box>.
        </>
      ),
    },
    { Icon: LockIcon, text: "The call is locked in before you reveal the next card." },
    { Icon: PointIcon, text: "A correct Higher or Lower scores 1 point. A correct Tie scores 2." },
    {
      Icon: RoundsIcon,
      text: `${numberOfCards} cards per run (${calls} calls), then the next player takes the hot seat.`,
    },
  ];

  return (
    <StepShell>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.35fr 1fr" },
            columnGap: { md: 5 },
            alignItems: "center",
          }}
        >
          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 1 } }}>
            <Eyebrow sx={{ mb: 1.5 }}>{waiting ? "Play with Host" : "Play with Host · Step 1"}</Eyebrow>
            <Typography
              component="h2"
              sx={{
                fontFamily: HOL.fontDisplay,
                fontWeight: 900,
                lineHeight: 0.95,
                letterSpacing: "-0.02em",
                fontSize: { xs: "2.6rem", sm: "3.4rem", md: "4rem" },
                mb: 1.5,
                ...holGradientText,
              }}
            >
              HIGHER
              <br />
              OR LOWER
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.3rem" },
                fontWeight: 600,
                color: "#fff",
                mb: { xs: 1, md: 0 },
              }}
            >
              How well do you know the odds?
            </Typography>
          </Box>

          <Box
            aria-hidden
            sx={{
              gridColumn: { xs: 1, md: 2 },
              gridRow: { md: "1 / span 2" },
              position: "relative",
              height: { xs: 200, sm: 240, md: 300 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              my: { xs: 1, md: 0 },
            }}
          >
            <CardImage suit="heart" fileValue="queen" width={{ xs: 110, sm: 140, md: 160 }} tilt={-9} sx={{ mr: -4, zIndex: 1 }} />
            <CardImage width={{ xs: 110, sm: 140, md: 160 }} tilt={8} sx={{ mt: 3 }} />
            <ArrowBadge Icon={HigherIcon} color={HOL.higher} sx={{ top: 8, right: { xs: "14%", sm: "10%" } }} />
            <ArrowBadge
              Icon={LowerIcon}
              color={HOL.lower}
              sx={{ bottom: 4, left: { xs: "14%", sm: "8%" }, animationDelay: "1.5s" }}
            />
          </Box>

          <Box sx={{ gridColumn: { md: 1 }, gridRow: { md: 2 }, mt: { md: 3 } }}>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: 4, display: "grid", gap: 1.5 }}>
              {concept.map(({ Icon, text }, i) => (
                <Box component="li" key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: "10px",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(255,255,255,0.06)",
                      border: `1px solid ${HOL.panelBorder}`,
                      color: HOL.gold,
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography sx={{ color: "rgba(255,255,255,0.86)", fontSize: "1rem", lineHeight: 1.5, pt: 0.5 }}>
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>

            {waiting ? (
              <Typography
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2.5,
                  py: 1.25,
                  borderRadius: "14px",
                  fontFamily: HOL.fontDisplay,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  color: HOL.gold,
                  bgcolor: "rgba(245,215,110,0.1)",
                  border: `1px solid ${HOL.gold}55`,
                  animation: `${holPulse} 2.2s ease-in-out infinite`,
                }}
              >
                Waiting for host to start
              </Typography>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<NextIcon />}
                  onClick={onNext}
                  autoFocus
                  sx={{ ...holPrimaryBtnSx, px: { xs: 4, sm: 6 }, fontSize: { xs: "1rem", sm: "1.1rem" } }}
                >
                  Start
                </Button>
                <Typography sx={{ color: HOL.muted, fontSize: "0.82rem", mt: 1.25 }}>
                  Nothing goes live yet. Next you&apos;ll walk through how the game is played.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </StepShell>
  );
};

export default HolIntroStep;
